import {useEffect, useMemo, useRef} from 'react';

import {Period} from '@deviceinsight/ng-ui-components';

import useMultipleKpiValues from '../common/hooks/useMultipleKpiValues';
import ChartWidgetContent from '../common/components/ChartWidgetContent';
import {AggregationSettings, SingleValueChartData} from '../common/types';
import {createChart, updateChart} from './chartUtils';
import {KpiPieChartWidgetConfigSettings, KpiPieChartWidgetProps} from './types';
import {createChartData, createFooterLabel, UPDATE_WIDGET_INTERVAL} from '../common/kpiUtils';
import {OutdatedConfigError} from '../common/components/kpiWidgetErrors';
import useAssetsFromConfig from '../common/hooks/useAssetsFromConfig';
import {nonNull} from '../../../../utils/tsUtils';

const createAggregationSettings = (
	settings: KpiPieChartWidgetConfigSettings,
	dashboardPeriod: Period | null | undefined
): AggregationSettings => ({
	items: settings.items
		.map(({assetUrn, kpiName}) =>
			assetUrn && kpiName
				? {
						assetUrn,
						kpiName
				  }
				: null
		)
		.filter(nonNull),
	aggregationType: settings.aggregationType,
	query: {
		periodTimeframe: dashboardPeriod ? 'CUSTOM' : settings.periodTimeframe,
		periodType: settings.periodType,
		periodCount: settings.periodCount,
		customPeriodFrom: dashboardPeriod ? dashboardPeriod.tsStart : settings.customPeriodFrom,
		customPeriodTo: dashboardPeriod ? dashboardPeriod.tsEnd : settings.customPeriodTo,
		workingShiftEnabled: settings.workingShiftEnabled,
		workingShiftDays: settings.workingShiftDays,
		workingShiftTimeFrom: settings.workingShiftTimeFrom,
		workingShiftTimeTo: settings.workingShiftTimeTo
	}
});

const KpiPieChartWidget = ({
	context,
	config: {key, settings, title},
	editMode,
	dashboardTime,
	dashboardPeriod
}: KpiPieChartWidgetProps) => {
	const {items, unit, periodTimeframe} = settings;
	const assetGroupContext = context.type === 'assetGroup';
	const kpiNames = settings.items.map((item) => item.kpiName).join(', ');
	const aggregationSettings = useMemo(
		() => createAggregationSettings(settings, dashboardPeriod),
		[settings, dashboardPeriod]
	);
	const {values, fetching, errorMessage, lastUpdate, reload} = useMultipleKpiValues(
		context,
		aggregationSettings,
		key,
		dashboardTime,
		editMode
	);
	const chartDataSets = useMemo<SingleValueChartData[]>(
		() => createChartData(items, values, context.type === 'asset' ? context.asset.urn : null),
		[context, items, values]
	);
	const containerRef = useRef(null);
	const chartRef = useRef(null);
	const assets = useAssetsFromConfig(items, context);
	useEffect(() => {
		const timeoutId = setTimeout(reload, UPDATE_WIDGET_INTERVAL);
		return () => clearTimeout(timeoutId);
	}, [reload, lastUpdate]);
	useEffect(() => {
		if (!containerRef.current) {
			return;
		}

		const chart = chartRef.current;

		if (context.type === 'asset' || items.every(({assetUrn}) => assets.some(({urn}) => urn === assetUrn))) {
			if (chart && chartDataSets.length > 0 && !editMode) {
				updateChart(chart, chartDataSets, assetGroupContext, assets);
			} else {
				chartRef.current = createChart(containerRef.current, chartDataSets, unit, assetGroupContext, assets);
			}
		}
	}, [assetGroupContext, chartDataSets, editMode, unit, assets, items, context]);

	if (periodTimeframe) {
		const footerLabel = createFooterLabel(aggregationSettings, dashboardTime);
		return (
			<ChartWidgetContent
				title={title}
				loading={fetching}
				containerRef={containerRef}
				dashboardTime={dashboardTime}
				editMode={editMode}
				footerLabel={footerLabel}
				kpiErrorMessage={errorMessage}
				kpiNames={kpiNames}
				lastUpdate={lastUpdate}
			/>
		);
	} else {
		return <OutdatedConfigError />;
	}
};

export default KpiPieChartWidget;
